import { createSelector } from 'redux-orm';

import orm from '../orm';
import { isLocalId } from '../utils/local-id';
import { selectPath } from './router';
import { calculateCoefficient } from '../utils/calculateCoefficient';
import { selectCurrentUser, selectCurrentUserId } from './users';
import { selectConfig } from './root';
export const makeSelectListById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ List }, id) => {
      const listModel = List.withId(id);

      if (!listModel) {
        return listModel;
      }

      return {
        ...listModel.ref,
        isPersisted: !isLocalId(id),
      };
    },
  );

export const selectListById = makeSelectListById();

export const makeSelectCardIdsByListId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ List }, id) => {
      const listModel = List.withId(id);

      if (!listModel) {
        return listModel;
      }

      return listModel.getFilteredOrderedCardsModelArray().filter(v => v.is_archive != true).map((cardModel) => cardModel.id);
    },
  );

export const selectCardIdsByListId = makeSelectCardIdsByListId();

export const makeSelectArchivedLists = () => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    (state) => selectCurrentUser(state),
    (state) => selectConfig(state),
    ({ List, Board }, projectId, currentUser, config) => {
      let boardsIds = Board.all().filter(v => v.projectId == projectId).toModelArray().map(v => v.id);
      let lists = List.all().filter(v => (v.is_archive == true && boardsIds.indexOf(v.boardId) > -1)).toModelArray();
      
      // Фильтрация по создателю для обычных пользователей
      if (currentUser) {
        const isSAdmin = currentUser.isSAdmin === true;
        const superuserIds = Array.isArray(config?.superuserIds) ? config.superuserIds : [];
        const isSuperuser = currentUser.id && superuserIds.map(String).includes(String(currentUser.id));
        
        if (!isSAdmin && !isSuperuser) {
          // Обычные пользователи видят только свои элементы
          lists = lists.filter(v => v.creatorUserId === currentUser.id);
        }
        // Суперпользователи видят все элементы (без фильтрации)
      }
      
      return lists.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });
    }
  );

export const selectArchivedLists = makeSelectArchivedLists();


export const makeSelectArchivedCards = (state) => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    (state) => selectCurrentUser(state),
    (state) => selectConfig(state),
    ({ Card, Board }, projectId, currentUser, config) => {
      let boardsIds = Board.all().filter(v => v.projectId == projectId).toModelArray().map(v => v.id);
      let cards = Card.all().filter(v => (v.is_archive == true && boardsIds.indexOf(v.boardId) > -1)).toModelArray();
      
      // Фильтрация по создателю для обычных пользователей
      if (currentUser) {
        const isSAdmin = currentUser.isSAdmin === true;
        const superuserIds = Array.isArray(config?.superuserIds) ? config.superuserIds : [];
        const isSuperuser = currentUser.id && superuserIds.map(String).includes(String(currentUser.id));
        
        if (!isSAdmin && !isSuperuser) {
          // Обычные пользователи видят только свои элементы
          cards = cards.filter(v => v.creatorUserId === currentUser.id);
        }
        // Суперпользователи видят все элементы (без фильтрации)
      }
      
      return cards.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });
    }
  );

export const selectArchivedCards = makeSelectArchivedCards();

export const makeSelectArchivedBoards = () => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    (state) => selectCurrentUser(state),
    (state) => selectConfig(state),
    ({ Board }, id, currentUser, config) => {
      let boards = Board.all().filter(v => v.is_archive == true && v.projectId == id).toModelArray();
      
      // Фильтрация по создателю для обычных пользователей
      if (currentUser) {
        const isSAdmin = currentUser.isSAdmin === true;
        const superuserIds = Array.isArray(config?.superuserIds) ? config.superuserIds : [];
        const isSuperuser = currentUser.id && superuserIds.map(String).includes(String(currentUser.id));
        
        if (!isSAdmin && !isSuperuser) {
          // Обычные пользователи видят только свои элементы
          boards = boards.filter(v => v.creatorUserId === currentUser.id);
        }
        // Суперпользователи видят все элементы (без фильтрации)
      }
      
      return boards.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });
    }
  );

export const selectArchivedBoards = makeSelectArchivedBoards();

export const makeSelectListPointsSum = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    ({ List, Card, CardPointData, CardPointAdvancedData, ContentCardDefault, ContentViewPlan, DampingCoefficient }, listId, pointSystemId) => {
      if (!listId || !pointSystemId) {
        return 0;
      }

      // Добавляем явную зависимость от DampingCoefficients для пересчета селектора
      // Используем toModelArray() для получения моделей, которые отслеживаются ORM
      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();
      
      console.log(`[selectListPointsSum] listId: ${listId}, pointSystemId: ${pointSystemId}, dampingCoefficientModels.length:`, dampingCoefficientModels.length);
      
      // Получаем хеш всех значений для явной зависимости от изменений
      // Включаем количество моделей в хеш, чтобы селектор пересчитывался при добавлении/удалении
      const dampingCoefficientsHash = dampingCoefficientModels.length > 0
        ? `${dampingCoefficientModels.length}:${dampingCoefficientModels
            .map(dc => `${dc.id}:${dc.from}:${dc.to}:${dc.value}`)
            .sort()
            .join('|')}`
        : '0';
      
      console.log(`[selectListPointsSum] listId: ${listId}, dampingCoefficientsHash:`, dampingCoefficientsHash);
      
      // Используем хеш для явной зависимости - это заставляет селектор пересчитываться
      // при изменении количества или значений DampingCoefficients
      const _dampingCoefficientsHash = dampingCoefficientsHash;

      const listModel = List.withId(listId);
      if (!listModel) {
        return 0;
      }

      // Получаем текущий месяц и год
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12

      // Получаем ContentViewPlan для текущего месяца/года
      // Используем toModelArray() для отслеживания изменений
      const contentViewPlanModels = ContentViewPlan.all()
        .filter({
          pointSistemId: pointSystemId,
          year: currentYear,
          month: currentMonth,
        })
        .toModelArray();
      const contentViewPlanModel = contentViewPlanModels.length > 0 ? contentViewPlanModels[0] : null;
      const contentViewPlan = contentViewPlanModel?.ref || null;
      
      // Добавляем зависимость от ContentViewPlan для пересчета селектора
      const contentViewPlanHash = contentViewPlanModel
        ? `${contentViewPlanModel.id}:${contentViewPlanModel.year}:${contentViewPlanModel.month}:${contentViewPlanModel.photo}:${contentViewPlanModel.video}:${contentViewPlanModel.caseCount}`
        : '0';

      // Получаем DampingCoefficients для данной системы баллов
      // Используем уже полученные модели из dampingCoefficientModels
      const dampingCoefficients = dampingCoefficientModels
        .sort((a, b) => (a.id || 0) - (b.id || 0))
        .map(model => model.ref);

      const cardModels = listModel.getFilteredOrderedCardsModelArray().filter(v => v.is_archive != true);
      let sum = 0;

      cardModels.forEach((cardModel) => {
        const cardId = cardModel.id;

        // Получаем CardPointData для карточки
        const cardPointDataModel = CardPointData.all()
          .filter({ cardId, pointSistemId: pointSystemId })
          .first();
        const cardPointData = cardPointDataModel?.ref || null;

        // Получаем ContentCardDefault через ccdId
        let contentCardDefault = null;
        if (cardPointDataModel?.ccdId) {
          const contentCardDefaultModel = ContentCardDefault.withId(cardPointDataModel.ccdId);
          // Проверяем, что ContentCardDefault существует и принадлежит правильному pointSystemId
          if (
            contentCardDefaultModel &&
            contentCardDefaultModel.pointSistemId === pointSystemId
          ) {
            contentCardDefault = contentCardDefaultModel.ref;
          }
        }

        // Рассчитываем коэффициент
        let coefficient = 1;
        if (contentCardDefault && cardPointData && dampingCoefficients.length > 0) {
          coefficient = calculateCoefficient({
            contentCardDefault,
            cardPointData,
            contentViewPlan,
            dampingCoefficients,
          });
        }

        // Считаем базовые баллы
        let basePoints = 0;
        if (contentCardDefault && contentCardDefault.value) {
          basePoints = contentCardDefault.value;
        }

        // Получаем все CardPointAdvancedData для карточки и суммируем их value
        const cardPointAdvancedDataModels = CardPointAdvancedData.all()
          .filter({ cardId, pointSistemId: pointSystemId })
          .toModelArray();

        // Если нет ни CardPointData, ни дополнительных баллов — пропускаем карточку
        if (!cardPointDataModel && cardPointAdvancedDataModels.length === 0) {
          return;
        }

        let extraPoints = 0;
        cardPointAdvancedDataModels.forEach((advancedDataModel) => {
          if (advancedDataModel.value) {
            extraPoints += advancedDataModel.value || 0;
          }
        });

        // Применяем коэффициент: totalPoints = (basePoints + extraPoints) * coefficient
        const totalPoints = (basePoints + extraPoints) * coefficient;
        console.log(`[selectListPointsSum] cardId: ${cardId}, basePoints: ${basePoints}, extraPoints: ${extraPoints}, coefficient: ${coefficient}, totalPoints: ${totalPoints}`);
        sum += totalPoints;
      });

      // Используем хеш для явной зависимости - это заставляет селектор пересчитываться
      // при изменении DampingCoefficients и ContentViewPlan
      // Хеш используется в вычислении через очень маленькое значение, которое не влияет на сумму,
      // но заставляет селектор пересчитываться при изменении хеша
      // Используем длину хеша как часть вычисления (умножаем на 0, чтобы не влиять на результат)
      const hashLength = _dampingCoefficientsHash.length + contentViewPlanHash.length;
      console.log(`[selectListPointsSum] listId: ${listId}, итоговая sum: ${sum}, hashLength: ${hashLength}`);
      return sum + (hashLength * 0);
    },
  );

export const selectListPointsSum = makeSelectListPointsSum();

export const makeSelectListPointsSumExcludingOtherMonth = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    ({ List, Card, CardPointData, CardPointArchivedData, CardPointAdvancedData, ContentCardDefault, ContentViewPlan, DampingCoefficient, ListReportData }, listId, pointSystemId) => {
      if (!listId || !pointSystemId) {
        return 0;
      }

      // Получаем ListReportData для проверки установленного месяца
      const listReportDataModel = ListReportData.all()
        .filter({ listId })
        .first();
      
      // Если месяц не установлен, возвращаем 0
      if (!listReportDataModel || !listReportDataModel.year || !listReportDataModel.month) {
        return 0;
      }

      const reportYear = listReportDataModel.year;
      const reportMonth = listReportDataModel.month;

      // Добавляем явную зависимость от DampingCoefficients для пересчета селектора
      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();
      
      // Получаем хеш всех значений для явной зависимости от изменений
      const dampingCoefficientsHash = dampingCoefficientModels.length > 0
        ? `${dampingCoefficientModels.length}:${dampingCoefficientModels
            .map(dc => `${dc.id}:${dc.from}:${dc.to}:${dc.value}`)
            .sort()
            .join('|')}`
        : '0';
      
      const _dampingCoefficientsHash = dampingCoefficientsHash;

      const listModel = List.withId(listId);
      if (!listModel) {
        return 0;
      }

      // Получаем ContentViewPlan для месяца из listReportData
      // Используем toModelArray() для отслеживания изменений
      const contentViewPlanModels = ContentViewPlan.all()
        .filter({
          pointSistemId: pointSystemId,
          year: reportYear,
          month: reportMonth,
        })
        .toModelArray();
      const contentViewPlanModel = contentViewPlanModels.length > 0 ? contentViewPlanModels[0] : null;
      const contentViewPlan = contentViewPlanModel?.ref || null;
      
      // Добавляем зависимость от ContentViewPlan для пересчета селектора
      const contentViewPlanHash = contentViewPlanModel
        ? `${contentViewPlanModel.id}:${contentViewPlanModel.year}:${contentViewPlanModel.month}:${contentViewPlanModel.photo}:${contentViewPlanModel.video}:${contentViewPlanModel.caseCount}`
        : '0';

      // Получаем DampingCoefficients для данной системы баллов
      const dampingCoefficients = dampingCoefficientModels
        .sort((a, b) => (a.id || 0) - (b.id || 0))
        .map(model => model.ref);

      const cardModels = listModel.getFilteredOrderedCardsModelArray().filter(v => v.is_archive != true);
      let sum = 0;

      cardModels.forEach((cardModel) => {
        const cardId = cardModel.id;

        // Получаем CardPointData для карточки
        const cardPointDataModel = CardPointData.all()
          .filter({ cardId, pointSistemId: pointSystemId })
          .first();

        if (!cardPointDataModel) {
          return;
        }

        const cardPointData = cardPointDataModel.ref;

        // Проверяем, закрыта ли задача и закрыта ли она в другом месяце
        if (cardPointData.closed !== true) {
          return;
        }

        const archivedModels = CardPointArchivedData.all()
          .filter({ cpdId: cardPointDataModel.id, pointSistemId: pointSystemId })
          .toModelArray();

        const archivedLatest = archivedModels && archivedModels.length > 0
          ? archivedModels.reduce((acc, model) => {
              const a = acc?.ref?.taskClosedAt ? new Date(acc.ref.taskClosedAt).getTime() : -Infinity;
              const b = model?.ref?.taskClosedAt ? new Date(model.ref.taskClosedAt).getTime() : -Infinity;
              return b > a ? model : acc;
            }, null)
          : null;

        const taskClosedAtRaw = archivedLatest?.ref?.taskClosedAt || cardPointData.closedAt;
        if (!taskClosedAtRaw) {
          return;
        }

        // Проверяем месяц закрытия задачи (task_closed_at)
        const closedAtDate = new Date(taskClosedAtRaw);
        const closedYear = closedAtDate.getFullYear();
        const closedMonth = closedAtDate.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12

        // Если задача закрыта в том же месяце, что и выбранный месяц, не учитываем её
        if (closedYear === reportYear && closedMonth === reportMonth) {
          return;
        }

        // Получаем ContentCardDefault через ccdId
        let contentCardDefault = null;
        if (cardPointDataModel.ccdId) {
          const contentCardDefaultModel = ContentCardDefault.withId(cardPointDataModel.ccdId);
          if (
            contentCardDefaultModel &&
            contentCardDefaultModel.pointSistemId === pointSystemId
          ) {
            contentCardDefault = contentCardDefaultModel.ref;
          }
        }

        // Рассчитываем коэффициент
        let coefficient = 1;
        if (contentCardDefault && cardPointData && dampingCoefficients.length > 0) {
          coefficient = calculateCoefficient({
            contentCardDefault,
            cardPointData,
            contentViewPlan,
            dampingCoefficients,
          });
        }

        // Считаем базовые баллы
        let basePoints = 0;
        if (contentCardDefault && contentCardDefault.value) {
          basePoints = contentCardDefault.value;
        }

        // Получаем все CardPointAdvancedData для карточки и суммируем их value
        const cardPointAdvancedDataModels = CardPointAdvancedData.all()
          .filter({ cardId, pointSistemId: pointSystemId })
          .toModelArray();

        let extraPoints = 0;
        cardPointAdvancedDataModels.forEach((advancedDataModel) => {
          if (advancedDataModel.value) {
            extraPoints += advancedDataModel.value || 0;
          }
        });

        // Применяем коэффициент: totalPoints = (basePoints + extraPoints) * coefficient
        const totalPoints = (basePoints + extraPoints) * coefficient;
        sum += totalPoints;
      });

      const hashLength = _dampingCoefficientsHash.length + contentViewPlanHash.length;
      return sum + (hashLength * 0);
    },
  );

export const selectListPointsSumExcludingOtherMonth = makeSelectListPointsSumExcludingOtherMonth();

export const makeSelectListClosedPointsSumExcludingOtherMonth = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    ({ List, Card, CardPointData, CardPointArchivedData, CardPointAdvancedData, ContentCardDefault, ContentViewPlan, DampingCoefficient, ListReportData }, listId, pointSystemId) => {
      if (!listId || !pointSystemId) {
        return 0;
      }

      // Получаем ListReportData для проверки установленного месяца
      const listReportDataModel = ListReportData.all()
        .filter({ listId })
        .first();
      
      // Если месяц не установлен, возвращаем 0
      if (!listReportDataModel || !listReportDataModel.year || !listReportDataModel.month) {
        return 0;
      }

      const reportYear = listReportDataModel.year;
      const reportMonth = listReportDataModel.month;

      // Добавляем явную зависимость от DampingCoefficients для пересчета селектора
      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();
      
      // Получаем хеш всех значений для явной зависимости от изменений
      const dampingCoefficientsHash = dampingCoefficientModels.length > 0
        ? `${dampingCoefficientModels.length}:${dampingCoefficientModels
            .map(dc => `${dc.id}:${dc.from}:${dc.to}:${dc.value}`)
            .sort()
            .join('|')}`
        : '0';
      
      const _dampingCoefficientsHash = dampingCoefficientsHash;

      const listModel = List.withId(listId);
      if (!listModel) {
        return 0;
      }

      // Получаем ContentViewPlan для месяца из listReportData
      // Используем toModelArray() для отслеживания изменений
      const contentViewPlanModels = ContentViewPlan.all()
        .filter({
          pointSistemId: pointSystemId,
          year: reportYear,
          month: reportMonth,
        })
        .toModelArray();
      const contentViewPlanModel = contentViewPlanModels.length > 0 ? contentViewPlanModels[0] : null;
      const contentViewPlan = contentViewPlanModel?.ref || null;
      
      // Добавляем зависимость от ContentViewPlan для пересчета селектора
      const contentViewPlanHash = contentViewPlanModel
        ? `${contentViewPlanModel.id}:${contentViewPlanModel.year}:${contentViewPlanModel.month}:${contentViewPlanModel.photo}:${contentViewPlanModel.video}:${contentViewPlanModel.caseCount}`
        : '0';

      // Получаем DampingCoefficients для данной системы баллов
      const dampingCoefficients = dampingCoefficientModels
        .sort((a, b) => (a.id || 0) - (b.id || 0))
        .map(model => model.ref);

      const cardModels = listModel.getFilteredOrderedCardsModelArray().filter(v => v.is_archive != true);
      let sum = 0;

      cardModels.forEach((cardModel) => {
        const cardId = cardModel.id;

        // Получаем CardPointData для карточки
        const cardPointDataModel = CardPointData.all()
          .filter({ cardId, pointSistemId: pointSystemId })
          .first();

        if (!cardPointDataModel) {
          return;
        }

        const cardPointData = cardPointDataModel.ref;

        // Учитываем только закрытые задачи
        if (cardPointData.closed !== true) {
          return;
        }

        const archivedModels = CardPointArchivedData.all()
          .filter({ cpdId: cardPointDataModel.id, pointSistemId: pointSystemId })
          .toModelArray();

        const archivedLatest = archivedModels && archivedModels.length > 0
          ? archivedModels.reduce((acc, model) => {
              const a = acc?.ref?.taskClosedAt ? new Date(acc.ref.taskClosedAt).getTime() : -Infinity;
              const b = model?.ref?.taskClosedAt ? new Date(model.ref.taskClosedAt).getTime() : -Infinity;
              return b > a ? model : acc;
            }, null)
          : null;

        const taskClosedAtRaw = archivedLatest?.ref?.taskClosedAt || cardPointData.closedAt;
        if (!taskClosedAtRaw) {
          return;
        }

        // Проверяем месяц закрытия задачи (task_closed_at)
        const closedAtDate = new Date(taskClosedAtRaw);
        const closedYear = closedAtDate.getFullYear();
        const closedMonth = closedAtDate.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12

        // Исключаем задачи, закрытые в другом месяце
        if (closedYear !== reportYear || closedMonth !== reportMonth) {
          return;
        }

        // Получаем ContentCardDefault через ccdId
        let contentCardDefault = null;
        if (cardPointDataModel.ccdId) {
          const contentCardDefaultModel = ContentCardDefault.withId(cardPointDataModel.ccdId);
          if (
            contentCardDefaultModel &&
            contentCardDefaultModel.pointSistemId === pointSystemId
          ) {
            contentCardDefault = contentCardDefaultModel.ref;
          }
        }

        // Рассчитываем коэффициент
        let coefficient = 1;
        if (contentCardDefault && cardPointData && dampingCoefficients.length > 0) {
          coefficient = calculateCoefficient({
            contentCardDefault,
            cardPointData,
            contentViewPlan,
            dampingCoefficients,
          });
        }

        // Считаем базовые баллы
        let basePoints = 0;
        if (contentCardDefault && contentCardDefault.value) {
          basePoints = contentCardDefault.value;
        }

        // Получаем все CardPointAdvancedData для карточки и суммируем их value
        const cardPointAdvancedDataModels = CardPointAdvancedData.all()
          .filter({ cardId, pointSistemId: pointSystemId })
          .toModelArray();

        let extraPoints = 0;
        cardPointAdvancedDataModels.forEach((advancedDataModel) => {
          if (advancedDataModel.value) {
            extraPoints += advancedDataModel.value || 0;
          }
        });

        // Применяем коэффициент: totalPoints = (basePoints + extraPoints) * coefficient
        const totalPoints = (basePoints + extraPoints) * coefficient;
        sum += totalPoints;
      });

      const hashLength = _dampingCoefficientsHash.length + contentViewPlanHash.length;
      return sum + (hashLength * 0);
    },
  );

export const selectListClosedPointsSumExcludingOtherMonth = makeSelectListClosedPointsSumExcludingOtherMonth();

export default {
  makeSelectListById,
  selectListById,
  selectArchivedLists,
  makeSelectArchivedLists,
  selectArchivedCards,
  makeSelectArchivedCards,
  selectArchivedBoards,
  makeSelectArchivedBoards,
  makeSelectCardIdsByListId,
  selectCardIdsByListId,
  makeSelectListPointsSum,
  selectListPointsSum,
  makeSelectListPointsSumExcludingOtherMonth,
  selectListPointsSumExcludingOtherMonth,
  makeSelectListClosedPointsSumExcludingOtherMonth,
  selectListClosedPointsSumExcludingOtherMonth,

};
